import { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'
import { v4 as uuid4 } from 'uuid'

import { VariableTextFeatureFlag } from '@makyoapp/makyo-common'
import { Search16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { DDItem, SimpleInput } from '@makyoapp/makyo-ui'

import { useGlobalVariables } from '@/hooks/useGlobalVariables'
import { ActionInputdata, NodeOutputOption } from '@/shared/action.types'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { appIconKeys } from '@/shared/icons'

import { VariableWrapper } from './VariableWrapper'

export default function VariableGlobalVariables({
  id,
  value,
  onChange,
  onHide,
  options,
}: {
  id?: string
  value?: ActionInputdata
  onChange: (value: { id: string; flag: VariableTextFeatureFlag.GlobalVariable; value: string }) => void
  onHide: () => void
  options?: NodeOutputOption[]
}) {
  const [show, setShow] = useBoolean(false)
  const [search, setSearch] = useState('')

  const { globalVariables } = useGlobalVariables()
  const showSearch = globalVariables.length > SHOW_SEARCH_LIMIT

  const filteredGlobalVariables = useMemo(() => {
    return globalVariables.filter((item) => item?.toLowerCase().includes(search.toLowerCase()))
  }, [globalVariables, search])

  // Reset navigation when dropdown is closed
  const handleSetShow = (value: boolean | ((prevState: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(show) : value
    setShow(newValue)
    if (!newValue) {
      setSearch('')
    }
  }

  return (
    <VariableWrapper
      id={id}
      show={show}
      setShow={handleSetShow}
      title={<Trans>Global Variables</Trans>}
      icon={<MaterialIcon size={16} icon={appIconKeys.globalVariable} />}
    >
      <div>
        {showSearch && (
          <div className='flex border-b border-zinc-200 dark:border-slate-600'>
            <SimpleInput
              noBorder
              outlined
              className='mk-search-box pr-3!'
              placeholder='Search...'
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value)
              }}
              showClear={!!search}
              onClear={() => {
                setSearch('')
              }}
              before={<Search16 className='mk-search-box-icon' />}
            />
          </div>
        )}

        <div className={clsx('max-h-[200px] overflow-y-auto')}>
          {!filteredGlobalVariables?.length ? (
            <div className='py-2 px-4 text-light'>
              <Trans>No global variables found</Trans>
            </div>
          ) : (
            filteredGlobalVariables.map((variableName, index) => {
              const isSelected = value?.value === variableName
              return (
                <DDItem
                  key={`${variableName}-${index}`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange({
                      id: uuid4(),
                      flag: VariableTextFeatureFlag.GlobalVariable,
                      value: variableName,
                    })
                  }}
                  className={clsx(
                    'w-full cursor-pointer hover:bg-zinc-100 dark:hover:bg-slate-700',
                    isSelected && 'bg-zinc-100 dark:bg-slate-700'
                  )}
                >
                  <div className='flex items-center justify-between w-full'>
                    <div className='flex flex-col flex-1'>
                      <div className='flex items-center gap-2'>
                        <MaterialIcon size={16} icon={appIconKeys.storage} />
                        <span>{variableName}</span>
                      </div>
                    </div>
                  </div>
                </DDItem>
              )
            })
          )}
        </div>
      </div>
    </VariableWrapper>
  )
}
